/**
 * Generates HTML for task comment notification emails
 * @param {Object} taskData - The task information
 * @param {Object} commenterData - Information about person who commented
 * @param {String} commentText - The text of the comment
 * @returns {String} HTML email content
 */
const generateTaskCommentEmailHTML = (taskData, commenterData, commentText) => {
  const taskTitle = taskData?.title || 'Untitled Task';
  const commenterName = commenterData?.name || commenterData?.email || 'A team member';
  const priorityColor = getPriorityColor(taskData?.priority);
  const statusLabel = getStatusLabel(taskData?.statusNormalized);
  const statusColor = getStatusColor(taskData?.statusNormalized);
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Comment on Task: ${taskTitle}</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f9fafb;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .email-header {
          background-color: #51796F;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-body {
          padding: 20px;
        }
        .email-footer {
          background-color: #f3f4f6;
          padding: 15px 20px;
          text-align: center;
          font-size: 12px;
          color: #6b7280;
        }
        .task-container {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
          background-color: #f9fafb;
        }
        .task-title {
          font-size: 18px;
          font-weight: 600;
          margin-top: 0;
          margin-bottom: 10px;
          color: #1f2937;
        }
        .task-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
        }
        .task-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .comment-container {
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-left: 4px solid #51796F;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .comment-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .commenter {
          font-weight: 600;
          color: #51796F;
        }
        .comment-date {
          color: #6b7280;
          font-size: 14px;
        }
        .comment-text {
          margin: 0;
          white-space: pre-wrap;
        }
        .action-button {
          display: inline-block;
          background-color: #51796F;
          color: white;
          text-decoration: none;
          padding: 10px 20px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 20px;
        }
        .disclaimer {
          margin-top: 20px;
          font-size: 12px;
          color: #6b7280;
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        <div class="email-header">
          <h1 style="margin: 0; font-size: 24px;">New Comment on Task</h1>
        </div>
        
        <div class="email-body">
          <p>Hello,</p>
          
          <p><strong>${commenterName}</strong> has added a comment to a task assigned to you:</p>
          
          <div class="task-container">
            <h2 class="task-title">${taskTitle}</h2>
            
            <div class="task-meta">
              <span class="task-badge" style="background-color: ${statusColor.bg}; color: ${statusColor.text};">
                ${statusLabel}
              </span>
              
              ${taskData.priority ? `
              <span class="task-badge" style="background-color: ${priorityColor.bg}; color: ${priorityColor.text};">
                ${taskData.priority} Priority
              </span>
              ` : ''}
              
              ${taskData.dueDate ? `
              <span class="task-badge" style="background-color: #f3f4f6; color: #4b5563;">
                Due: ${formatDate(taskData.dueDate)}
              </span>
              ` : ''}
            </div>
          </div>
          
          <div class="comment-container">
            <div class="comment-header">
              <span class="commenter">${commenterName}</span>
              <span class="comment-date">${currentDate}</span>
            </div>
            <p class="comment-text">${commentText}</p>
          </div>
          
          <a href="#" class="action-button">View Task</a>
          
          <p class="disclaimer">
            Please do not reply to this email. This message was automatically generated by the MyelinZ PPC system.
          </p>
        </div>
        
        <div class="email-footer">
          <p>&copy; ${new Date().getFullYear()} MyelinZ PPC. All rights reserved.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Helper functions
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  let date;
  if (dateValue.seconds) {
    // Firestore timestamp
    date = new Date(dateValue.seconds * 1000);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    // Try to parse string or number
    date = new Date(dateValue);
  }
  
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return { bg: '#fee2e2', text: '#b91c1c' };
    case 'medium':
      return { bg: '#fef3c7', text: '#b45309' };
    case 'low':
      return { bg: '#e0f2fe', text: '#0369a1' };
    default:
      return { bg: '#f3f4f6', text: '#4b5563' };
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'new':
      return { bg: '#e0f2fe', text: '#0369a1' };
    case 'in-progress':
      return { bg: '#fef3c7', text: '#b45309' };
    case 'completed':
      return { bg: '#dcfce7', text: '#16a34a' };
    default:
      return { bg: '#f3f4f6', text: '#4b5563' };
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'new':
      return 'New';
    case 'in-progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    default:
      return 'Unknown Status';
  }
};

export default generateTaskCommentEmailHTML; 